import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
  boolean,
  index,
  foreignKey,
  jsonb,
} from 'drizzle-orm/pg-core';
import { organization_website } from '@trashlab/api/src/domains/website/schemas/organization-website';
import { organization } from '@trashlab/api/src/domains/identity/schemas/organization';
import { isoTimestamp } from '@trashlab/api/src/domains/core/schemas/base-template';

export const domain_status = pgEnum('domain_status', [
  'ACTIVE',
  'PENDING',
  'INACTIVE',
]);

export const domain_registration_status = pgEnum('domain_registration_status', [
  'NOT_STARTED',
  'REGISTERED',
  'VERIFIED',
  'FAILED',
]);

export const organization_domain = pgTable(
  'organization_domain',
  {
    ...defaultFields.fieldsWithTenantId(),
    domain: varchar('domain', { length: 255 }).notNull().unique(),
    status: domain_status('status').default('PENDING').notNull(),
    siteId: uuid('siteId')
      .notNull()
      .references(() => organization_website.id),
    isDefault: boolean('isDefault').default(false).notNull(),
    dnsVerified: boolean('dnsVerified').default(false).notNull(),
    sslActive: boolean('sslActive').default(false).notNull(),
    isTestDomain: boolean('isTestDomain').default(false).notNull(),
    renderDomainId: varchar('renderDomainId', { length: 255 }),
    renderRegistrationStatus: domain_registration_status(
      'renderRegistrationStatus',
    )
      .default('NOT_STARTED')
      .notNull(),
    renderVerificationStatus: varchar('renderVerificationStatus', {
      length: 255,
    }),
    renderRegisteredAt: isoTimestamp('renderRegisteredAt', {
      withTimezone: true,
    }),
    renderLastCheckedAt: isoTimestamp('renderLastCheckedAt', {
      withTimezone: true,
    }),
    renderLastError: text('renderLastError'),
    sendgridDomainId: varchar('sendgridDomainId', { length: 255 }),
    sendgridRegistrationStatus: domain_registration_status(
      'sendgridRegistrationStatus',
    )
      .default('NOT_STARTED')
      .notNull(),
    sendgridVerificationStatus: varchar('sendgridVerificationStatus', {
      length: 255,
    }),
    sendgridSubdomain: varchar('sendgridSubdomain', { length: 63 }),
    sendgridDnsRecords: jsonb('sendgridDnsRecords'),
    sendgridRegisteredAt: isoTimestamp('sendgridRegisteredAt', {
      withTimezone: true,
    }),
    sendgridLastCheckedAt: isoTimestamp('sendgridLastCheckedAt', {
      withTimezone: true,
    }),
    sendgridLastError: text('sendgridLastError'),
  },
  (table) => ({
    createdAtTs_idx: index('organization_domain_createdAtTs_idx').on(
      table.createdAtTs,
    ),
    tenantId_idx: index('organization_domain_tenantId_idx').on(table.tenantId),
    updatedAtTs_idx: index('organization_domain_updatedAtTs_idx').on(
      table.updatedAtTs,
    ),
    updatedAtTs_idx1: index('organization_domain_updatedAtTs_idx1').on(
      table.updatedAtTs.desc(),
    ),
    versionNumber_idx: index('organization_domain_versionNumber_idx').on(
      table.versionNumber,
    ),
    domain_idx: index('organization_domain_domain_idx').on(table.domain),
    domain_trgm_idx: index('organization_domain_domain_trgm_idx').using(
      'gin',
      table.domain.op('gin_trgm_ops'),
    ),
    siteId_idx: index('organization_domain_siteId_idx').on(table.siteId),
    renderDomainId_idx: index('organization_domain_renderDomainId_idx').on(
      table.renderDomainId,
    ),
    renderRegistrationStatus_idx: index(
      'organization_domain_renderRegistrationStatus_idx',
    ).on(table.renderRegistrationStatus),
    sendgridDomainId_idx: index('organization_domain_sendgridDomainId_idx').on(
      table.sendgridDomainId,
    ),
    sendgridRegistrationStatus_idx: index(
      'organization_domain_sendgridRegistrationStatus_idx',
    ).on(table.sendgridRegistrationStatus),

    organizationId_fkey: foreignKey({
      columns: [table.tenantId],
      foreignColumns: [organization.id],
      name: 'organization_domain_organizationId_fkey',
    }),
  }),
);

export const organization_domain_history = pgTable(
  'organization_domain_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    domain: varchar('domain', { length: 255 }),
    status: domain_status('status'),
    siteId: uuid('siteId'),
    isDefault: boolean('isDefault'),
    dnsVerified: boolean('dnsVerified'),
    sslActive: boolean('sslActive'),
    isTestDomain: boolean('isTestDomain'),
    renderDomainId: varchar('renderDomainId', { length: 255 }),
    renderRegistrationStatus: domain_registration_status(
      'renderRegistrationStatus',
    ),
    renderVerificationStatus: varchar('renderVerificationStatus', {
      length: 255,
    }),
    renderRegisteredAt: isoTimestamp('renderRegisteredAt', {
      withTimezone: true,
    }),
    renderLastCheckedAt: isoTimestamp('renderLastCheckedAt', {
      withTimezone: true,
    }),
    renderLastError: text('renderLastError'),
    sendgridDomainId: varchar('sendgridDomainId', { length: 255 }),
    sendgridRegistrationStatus: domain_registration_status(
      'sendgridRegistrationStatus',
    ),
    sendgridVerificationStatus: varchar('sendgridVerificationStatus', {
      length: 255,
    }),
    sendgridSubdomain: varchar('sendgridSubdomain', { length: 63 }),
    sendgridDnsRecords: jsonb('sendgridDnsRecords'),
    sendgridRegisteredAt: isoTimestamp('sendgridRegisteredAt', {
      withTimezone: true,
    }),
    sendgridLastCheckedAt: isoTimestamp('sendgridLastCheckedAt', {
      withTimezone: true,
    }),
    sendgridLastError: text('sendgridLastError'),
  },
);
